import {Injectable, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {timeout} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {OrganizationContextInterface} from '../../interfaces/organization-context.interface';
import {ResolveResultKindEnum} from '../../enums/resolve-result-kind.enum';
import {ResolveResultType} from '../../types/resolve-result.type';

const RESOLVE_TIMEOUT_MS = 5000;
const SESSION_STORAGE_PREFIX = 'wf_org_';
const ORIGIN_CONTEXT_PATH = '/v3/origin-context';

@Injectable({providedIn: 'root'})

export class RuntimeOrganizationService {
  private readonly _httpClient: HttpClient = inject(HttpClient);
  private readonly _cache: Map<string, OrganizationContextInterface> = new Map<string, OrganizationContextInterface>();

  constructor() {
    this.rehydrate();
  }

  public getCached(origin: string): OrganizationContextInterface | null {
    return this._cache.get(origin) ?? null;
  }

  public async resolveFromBff(origin: string): Promise<ResolveResultType> {
    const url = this.buildOriginContextUrl();
    try {
      await firstValueFrom(
        this._httpClient.get(url, {headers: {'X-Parent-Domain': origin}, observe: 'response'})
          .pipe(timeout(RESOLVE_TIMEOUT_MS))
      );
    } catch (error) {
      return {kind: ResolveResultKindEnum.Error, cause: error instanceof Error ? error : new Error(String(error))};
    }
    const cached = this.getCached(origin);
    if (cached) {
      return {kind: ResolveResultKindEnum.Org, context: cached};
    }
    return {kind: ResolveResultKindEnum.Master};
  }

  public setFromHeaders(origin: string, ctx: OrganizationContextInterface): void {
    this._cache.set(origin, ctx);
    sessionStorage.setItem(`${SESSION_STORAGE_PREFIX}${origin}`, JSON.stringify(ctx));
  }

  private rehydrate(): void {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith(SESSION_STORAGE_PREFIX)) {
        continue;
      }
      const origin = key.substring(SESSION_STORAGE_PREFIX.length);
      const raw = sessionStorage.getItem(key);
      if (!raw) {
        continue;
      }
      const ctx: OrganizationContextInterface = JSON.parse(raw);
      if (ctx && ctx.organizationId) {
        this._cache.set(origin, ctx);
      }
    }
  }

  private buildOriginContextUrl(): string {
    const base: string = environment.widgetApi.endsWith('/') ? environment.widgetApi.slice(0, -1) : environment.widgetApi;
    return `${base}${ORIGIN_CONTEXT_PATH}`;
  }
}
